import { useEffect, useState } from 'react';

/**
 * Trails `value` by `delayMs`: the returned value only moves once `value` has
 * been stable for the full delay. Use it to keep a fast-changing input (a search
 * box) from firing a request per keystroke.
 *
 * `flushWhen` lets a value skip the delay: when it returns true for the current
 * value, the returned value jumps to it at once and later changes trail from there.
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number,
  options?: { flushWhen?: (value: T) => boolean },
): T {
  const [debounced, setDebounced] = useState(value);

  // Adjusting state during render (not in an effect) means the flushed value is
  // what this very render returns, so no consumer sees the stale term.
  if (options?.flushWhen?.(value) && debounced !== value) {
    setDebounced(value);
  }

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}
